import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { BrainCircuit, AppWindow, Sparkles, Puzzle, Briefcase, 
  Presentation, Clock, Zap, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import MainLayout from "@/layouts/MainLayout";
import { AnimatedGradientBackground } from "@/components/ui/animated-gradient-background";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
} from "@/components/ui/card";
import { HoverSpotlight } from "@/components/ui/hover-spotlight";
import TrustedBy from "@/components/ui/trusted-by";
import Testimonials from "@/components/pages/home/Testimonials";
import AIBootcampValueCard from "@/components/AIBootcampValueCard";

const AIBootcamp = () => {
  const location = useLocation();

  useEffect(() => {
    // Ensure the page loads at the top when component mounts
    window.scrollTo(0, 0);
    
    // Check if there's a hash in the URL
    if (location.hash) {
      // Remove the # character to get the element id
      const id = location.hash.substring(1);
      const element = document.getElementById(id);
      
      if (element) {
        // Use setTimeout to ensure the page has fully loaded
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  return (
    <MainLayout
      title="AI for Business Bootcamp | Niumora"
      description="Master practical AI skills in just 5 days. Our AI Bootcamp teaches business owners and professionals how to implement powerful AI solutions to transform operations, boost efficiency, and create new opportunities."
      keywords="AI bootcamp, AI for business, artificial intelligence training, AI skills, business automation, Niumora training"
      ogType="website"
    >
      {/* Hero Section */}
      <AnimatedGradientBackground
        containerClassName="min-h-[800px] xl:min-h-[900px] 2xl:min-h-[1000px] max-h-[1200px] flex flex-col items-center justify-center pt-24"
        className="w-full"
        colors={[
          "rgba(56, 189, 248, 0.2)",  // Light blue
          "rgba(99, 102, 241, 0.25)", // Indigo
          "rgba(232, 121, 249, 0.2)", // Light pink
        ]}
      >
        <div className="container max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
            <div className="space-y-8">
              <Badge 
                variant="secondary" 
                className="px-4 py-1.5 text-sm font-medium bg-slate-200 text-slate-900 hover:bg-slate-200/80"
              >
                <span className="flex h-2 w-2 rounded-full bg-primary mr-2"></span>
                5-Day Exclusive Program
              </Badge>

              <div className="space-y-4">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-display tracking-tight">
                  <span className="block">
                    Learn <span className="gradient-text">AI for Business</span>
                  </span>
                  <span className="block">
                    Bootcamp
                  </span>
                </h1>
                
                <p className="text-xl text-muted-foreground max-w-xl">
                  Master practical AI tools and strategies to transform your business, create new opportunities, and boost efficiency in just 5 days.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="rounded-full px-8 hover:scale-105 transition-all duration-300"
                  onClick={() => {
                    const learnSection = document.getElementById('learn-section');
                    if (learnSection) {
                      learnSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                >
                  Learn More
                </Button>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-slate-800 rounded-2xl shadow-xl">
                <AIBootcampValueCard variant="dark" />
              </div>
              
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-secondary/20 rounded-full blur-xl hidden lg:block"></div>
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-primary/20 rounded-full blur-xl hidden lg:block"></div>
            </div>
          </div>
        </div>
        <TrustedBy className="mt-16" />
      </AnimatedGradientBackground>

      {/* Testimonials Section */}
      <section className="bg-white">
        <Testimonials />
      </section>

      {/* Who's This For Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-display mb-4">
              Who Is <span className="gradient-text">AI Bootcamp For?</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Whether you're a time-starved business owner desperate to reclaim your evenings, a service professional drowning in client demands, an ambitious entrepreneur with a brilliant idea but limited resources, or even a mid-career professional feeling left behind by the rapid pace of AI innovation—this bootcamp is your shortcut to practical AI mastery.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <HoverSpotlight
              className="p-8 rounded-xl bg-white border border-primary/10 shadow-sm"
              spotlightColor="rgba(99, 102, 241, 0.08)"
            >
              <div>
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-6">
                  <Briefcase className="h-7 w-7" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Time-pressed business owners</h3>
                <p className="text-muted-foreground">
                  Perfect for entrepreneurs who need practical AI solutions to automate tedious tasks and streamline daily operations without learning to code.
                </p>
              </div>
            </HoverSpotlight>
            
            <HoverSpotlight
              className="p-8 rounded-xl bg-white border border-secondary/10 shadow-sm"
              spotlightColor="rgba(168, 85, 247, 0.08)"
            >
              <div>
                <div className="w-14 h-14 rounded-full bg-secondary/10 flex items-center justify-center text-secondary mb-6">
                  <Presentation className="h-7 w-7" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Service-based professionals</h3>
                <p className="text-muted-foreground">
                  Ideal for consultants, coaches, and service providers who want to leverage AI to enhance client delivery, create premium offerings, and increase capacity.
                </p>
              </div>
            </HoverSpotlight>
            
            <HoverSpotlight
              className="p-8 rounded-xl bg-white border border-primary/10 shadow-sm"
              spotlightColor="rgba(99, 102, 241, 0.08)"
            >
              <div>
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-6">
                  <Zap className="h-7 w-7" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Ambitious entrepreneurs</h3>
                <p className="text-muted-foreground">
                  For founders with big ideas but limited technical resources who want to rapidly prototype, test, and deploy solutions without expensive development teams.
                </p>
              </div>
            </HoverSpotlight>
            
            <HoverSpotlight
              className="p-8 rounded-xl bg-white border border-secondary/10 shadow-sm"
              spotlightColor="rgba(168, 85, 247, 0.08)"
            >
              <div>
                <div className="w-14 h-14 rounded-full bg-secondary/10 flex items-center justify-center text-secondary mb-6">
                  <Clock className="h-7 w-7" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Mid-career professionals</h3>
                <p className="text-muted-foreground">
                  Perfect for those looking to future-proof their careers by mastering AI tools that make them more valuable, productive, and innovative in their current roles.
                </p>
              </div>
            </HoverSpotlight>
          </div>
        </div>
      </section>
      
      {/* What You'll Learn Section */}
      <section id="learn-section" className="py-16 md:py-24 bg-white">
        <div className="container max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-display mb-4">
              What You'll <span className="gradient-text">Learn</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Our focused curriculum gives you practical AI skills you can implement immediately in your business and everyday life.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="p-6 rounded-xl bg-white shadow-sm border border-slate-100">
              <CardHeader className="p-0 flex flex-col items-center">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-4">
                  <BrainCircuit className="h-6 w-6" />
                </div>
              </CardHeader>
              <CardContent className="p-0 text-center">
                <h3 className="text-xl font-semibold mb-2">AI Foundations</h3>
                <p className="text-muted-foreground">
                  Master the AI landscape including LLMs, Agents, Custom GPTs, and Vector Databases while ensuring data privacy and security.
                </p>
              </CardContent>
            </Card>
            
            <Card className="p-6 rounded-xl bg-white shadow-sm border border-slate-100">
              <CardHeader className="p-0 flex flex-col items-center">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-4">
                  <AppWindow className="h-6 w-6" />
                </div>
              </CardHeader>
              <CardContent className="p-0 text-center">
                <h3 className="text-xl font-semibold mb-2">Prompt Engineering</h3>
                <p className="text-muted-foreground">
                  Learn advanced prompting techniques and best practices to get the most out of AI models through hands-on exercises.
                </p>
              </CardContent>
            </Card>
            
            <Card className="p-6 rounded-xl bg-white shadow-sm border border-slate-100">
              <CardHeader className="p-0 flex flex-col items-center">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-4">
                  <Sparkles className="h-6 w-6" />
                </div>
              </CardHeader>
              <CardContent className="p-0 text-center">
                <h3 className="text-xl font-semibold mb-2">AI Agents & Automation</h3>
                <p className="text-muted-foreground">
                  Build powerful AI agents and automate workflows with no-code tools, thinking in processes and steps.
                </p>
              </CardContent>
            </Card>
            
            <Card className="p-6 rounded-xl bg-white shadow-sm border border-slate-100">
              <CardHeader className="p-0 flex flex-col items-center">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-4">
                  <Puzzle className="h-6 w-6" />
                </div>
              </CardHeader>
              <CardContent className="p-0 text-center">
                <h3 className="text-xl font-semibold mb-2">Vibe Coding</h3>
                <p className="text-muted-foreground">
                  Master vibe coding with Claude artifacts, APIs, and MCPs to build your own applications without traditional coding.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      
      {/* Curriculum Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container max-w-5xl mx-auto px-4 md:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-display mb-4">
              <span className="gradient-text">5-Day</span> Curriculum
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              An immersive, hands-on experience designed to give you practical AI skills in just 5 days. Each cohort is slightly tailored to its participants to create maximum impact.
            </p>
          </div>
          
          <div className="space-y-6 max-w-4xl mx-auto">
            {/* Day 1 */}
            <Card className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
              <CardHeader>
                <div className="flex-shrink-0 bg-[length:400%_100%] bg-gradient-to-r from-primary/5 via-primary/20 to-primary/5 animate-gradient-x py-8 px-6 flex items-center justify-center relative h-32">
                  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-sm">
                    <span className="text-2xl font-bold text-slate-700">1</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-4">Day 1: Foundation & AI Essentials</h3>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">Understanding the AI landscape for business application and personal use (LLMs, Agents, Automations, Custom GPTs, GenAI, Vector Databases & RAG)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">Data, privacy and security</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">How to Identify high-impact automation opportunities</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            
            {/* Day 2 */}
            <Card className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
              <CardHeader>
                <div className="flex-shrink-0 bg-[length:400%_100%] bg-gradient-to-r from-secondary/5 via-secondary/20 to-secondary/5 animate-gradient-x py-8 px-6 flex items-center justify-center relative h-32">
                  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-sm">
                    <span className="text-2xl font-bold text-slate-700">2</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-4">Day 2: Prompt Engineering</h3>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">Learn how to craft effective prompts to guide AI behavior and improve outputs</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">Explore prompting styles (zero-shot, few-shot, role, system) and best practices through hands-on exercises</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            
            {/* Day 3 */}
            <Card className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
              <CardHeader>
                <div className="flex-shrink-0 bg-[length:400%_100%] bg-gradient-to-r from-primary/5 via-primary/20 to-primary/5 animate-gradient-x py-8 px-6 flex items-center justify-center relative h-32">
                  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-sm">
                    <span className="text-2xl font-bold text-slate-700">3</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-4">Day 3: No-Code AI Agents and Automations</h3>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">Landscape, overview and use cases</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">Thinking in steps and processes</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">Hands-on Agent building</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            
            {/* Day 4 */}
            <Card className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
              <CardHeader>
                <div className="flex-shrink-0 bg-[length:400%_100%] bg-gradient-to-r from-secondary/5 via-secondary/20 to-secondary/5 animate-gradient-x py-8 px-6 flex items-center justify-center relative h-32">
                  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-sm">
                    <span className="text-2xl font-bold text-slate-700">4</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-4">Day 4: Vibe coding No-Code Development</h3>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">What is vibe coding, overview and use cases and claude artifacts</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">Security and safety</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">APIs and MCPs</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">Hands-on building your first app</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            
            {/* Day 5 */}
            <Card className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
              <CardHeader>
                <div className="flex-shrink-0 bg-[length:400%_100%] bg-gradient-to-r from-primary/5 via-primary/20 to-primary/5 animate-gradient-x py-8 px-6 flex items-center justify-center relative h-32">
                  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-sm">
                    <span className="text-2xl font-bold text-slate-700">5</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-4">Day 5: Tying it all together</h3>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">Final project of a real world solution for you</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      
      {/* Call to Action Section */}
      <section id="apply-form-section" className="py-16 md:py-24 bg-slate-900 text-white">
        <div className="container max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold font-display mb-4">
                Ready to Transform Your Business with AI?
              </h2>
              <p className="text-xl text-slate-300 max-w-xl">
                Secure your spot in our next cohort. Limited to 10 participants to ensure personalized attention and maximum results.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-primary mt-0.5" />
                  <p className="text-slate-300">Master practical AI skills in just 5 days</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-primary mt-0.5" />
                  <p className="text-slate-300">Implement real solutions during the bootcamp</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-primary mt-0.5" />
                  <p className="text-slate-300">Get 1 year of community support included</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-primary mt-0.5" />
                  <p className="text-slate-300">100% satisfaction guarantee</p>
                </div>
              </div>
            </div>
            
            <div className="bg-slate-800 rounded-2xl shadow-xl">
              <AIBootcampValueCard variant="dark" />
            </div>
          </div>
        </div>
      </section>
    </MainLayout>
  );
};

export default AIBootcamp;
